import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";
import { Info, Table, Trash2, Filter } from "lucide-react";
import { RunDataModal, StepDetailsModal, StepDataModal, ExpectationDataModal, ExpectationDetailsModal, RunLogsModal, RunDetailsModal } from "@/components/modals/run-modals";
import type { 
  Test, 
  TestRun, 
  RunGroup, 
  RunFilters, 
  RunsPagination, 
  RunsApiResponse,
  NewRunForm,
  RunStep,
  ExpectationResult
} from "@/lib/types/test";

// Utility functions
const formatDate = (dateString: string) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleString();
};

const getPassedCount = (run: TestRun): number => {
  // Use backend-provided count if available (for overview/group view)
  if (typeof run.expectationsPassed === 'number') {
    return run.expectationsPassed;
  }
  
  // Fallback to calculation from step data (for detailed view)
  if (!run.steps) return 0;
  
  return run.steps
    .filter(step => step.type.startsWith('TOOL_EVALUATE_EXPECTATIONS'))
    .reduce((count, step) => {
      if (step.result?.expectations) {
        return count + step.result.expectations.filter(
          (expectation: ExpectationResult) => expectation.runState === 'SUCCESS' && expectation.resultMatches
        ).length;
      }
      return count;
    }, 0);
};

const getFailedCount = (run: TestRun): number => {
  // Use backend-provided count if available (for overview/group view)
  if (typeof run.expectationsFailed === 'number') {
    return run.expectationsFailed;
  }
  
  // Fallback to calculation from step data (for detailed view)  
  if (!run.steps) return 0;
  
  return run.steps
    .filter(step => step.type.startsWith('TOOL_EVALUATE_EXPECTATIONS'))
    .reduce((count, step) => {
      if (step.result?.expectations) {
        return count + step.result.expectations.filter(
          (expectation: ExpectationResult) => expectation.runState === 'FAILURE' || (!expectation.resultMatches && expectation.foundResult)
        ).length;
      }
      return count;
    }, 0);
};

// Filter Modal Component
interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: RunFilters) => void;
  currentFilters: RunFilters;
}

function FilterModal({ isOpen, onClose, onApply, currentFilters }: FilterModalProps) {
  const [localFilters, setLocalFilters] = useState<RunFilters>(currentFilters);

  // Reset local filters when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(currentFilters);
    }
  }, [isOpen, currentFilters]);

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleReset = () => {
    const resetFilters: RunFilters = {
      state: 'All',
      dateRange: 'all',
      runId: ''
    };
    setLocalFilters(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-popover rounded-lg shadow-xl p-6 w-full max-w-lg border">
        <h3 className="text-lg font-semibold mb-4">Filter Runs</h3>
        
        <div className="space-y-4">
          {/* Run/Group ID Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Run/Group ID
            </label>
            <input
              type="text"
              placeholder="Search by run ID or group ID..."
              value={localFilters.runId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, runId: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* State Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              State
            </label>
            <select
              value={localFilters.state || 'All'}
              onChange={(e) => setLocalFilters({ 
                ...localFilters, 
                state: e.target.value as RunFilters['state'] 
              })}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="All">All States</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILURE">Failure</option>
              <option value="RUNNING">Running</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Date Range
            </label>
            <select
              value={localFilters.dateRange || 'all'}
              onChange={(e) => setLocalFilters({ 
                ...localFilters, 
                dateRange: e.target.value as RunFilters['dateRange'],
                // Reset date fields when changing date range type
                specificDate: e.target.value === 'specific' ? localFilters.specificDate : undefined,
                startDate: e.target.value === 'range' ? localFilters.startDate : undefined,
                endDate: e.target.value === 'range' ? localFilters.endDate : undefined
              })}
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last30">Last 30 Days</option>
              <option value="specific">Specific Date</option>
              <option value="range">Date Range</option>
            </select>
          </div>

          {/* Specific Date Picker */}
          {localFilters.dateRange === 'specific' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Select Date
              </label>
              <input
                type="date"
                value={localFilters.specificDate || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, specificDate: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          {/* Date Range Pickers */}
          {localFilters.dateRange === 'range' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={localFilters.startDate || ''}
                  onChange={(e) => setLocalFilters({ ...localFilters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={localFilters.endDate || ''}
                  onChange={(e) => setLocalFilters({ ...localFilters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="flex justify-between mt-6">
          <Button
            variant="secondary"
            onClick={handleReset}
            className="text-sm"
          >
            Reset
          </Button>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TestRunsPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  
  // Get URL search params for expansion state persistence
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [runGroups, setRunGroups] = useState<RunGroup[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isPollingUpdate, setIsPollingUpdate] = useState(false);
  
  // Get expansion state from URL params
  const expandedGroupId = searchParams.get('groupId');
  const expandedRunId = searchParams.get('runId');
  
  // Helper functions to update URL params
  const setExpandedGroupId = useCallback((groupId: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (groupId) {
      newParams.set('groupId', groupId);
    } else {
      newParams.delete('groupId');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);
  
  const setExpandedRunId = useCallback((runId: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (runId) {
      newParams.set('runId', runId);
    } else {
      newParams.delete('runId');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);
  const [pagination, setPagination] = useState<RunsPagination>({
    offset: 0,
    limit: 10,
    totalCount: 0,
    hasMore: false
  });
  const [filters, setFilters] = useState<RunFilters>({
    state: 'All',
    dateRange: 'all',
    runId: ''
  });
  const [showCreateRunModal, setShowCreateRunModal] = useState(false);
  const [newRunForm, setNewRunForm] = useState<NewRunForm>({ numberOfRuns: 1 });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Confirmation modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<RunGroup | null>(null);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  // Run modals
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRunDataModal, setShowRunDataModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState<TestRun | null>(null);
  
  // Step modals
  const [showStepDetailsModal, setShowStepDetailsModal] = useState(false);
  const [showStepDataModal, setShowStepDataModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState<RunStep | null>(null);
  const [showStepResultsJson, setShowStepResultsJson] = useState(false);
  
  // Expectation modals
  const [showExpectationDetailsModal, setShowExpectationDetailsModal] = useState(false);
  const [showExpectationDataModal, setShowExpectationDataModal] = useState(false);
  const [selectedExpectation, setSelectedExpectation] = useState<ExpectationResult | null>(null);
  
  // Track recently updated items for visual indicators (simplified for now)
  const [recentlyUpdatedRuns] = useState<Set<string>>(new Set());
  const [recentlyUpdatedSteps] = useState<Set<string>>(new Set());

  const loadTest = useCallback(async () => {
    if (!testId) return;
    
    try {
      setIsLoading(true);
      const testData = await api.get<Test>(`/test/${testId}`);
      setTest(testData);
    } catch (error) {
      console.error("Failed to load test:", error);
      showError("Failed to load test", "Could not load test details. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  }, [testId, showError, navigate]);

  // Load runs data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadRuns = useCallback(async (reset = false, currentFilters = filters, isPolling = false) => {
    if (!testId) return;
    
    try {
      if (isPolling) {
        setIsPollingUpdate(true);
      } else {
        setIsLoadingRuns(true);
      }
      
      if (reset) {
        setPagination(prev => ({ ...prev, offset: 0 }));
      }
      
      const offset = reset ? 0 : pagination.offset;
      const response = await api.getRuns(testId, {
        offset,
        limit: pagination.limit,
        ...currentFilters
      }) as RunsApiResponse;
      
      if (reset) {
        // Simple reset - just update the data and preserve current expansion states
        const newGroups = (response.groups || []).map(newGroup => ({
          ...newGroup,
          runs: newGroup.runs
        }));
        
        setRunGroups(newGroups);
        
        // Check if expanded group still exists
        if (expandedGroupId && !response.groups?.some(g => g.id === expandedGroupId)) {
          setExpandedGroupId(null);
          setExpandedRunId(null);
        }
        
        // Auto-expand for URL-specified items and load run details if needed
        if (expandedGroupId && !isPolling) {
          const targetGroup = response.groups?.find(g => g.id === expandedGroupId);
          
          if (targetGroup && expandedRunId) {
            // Only load run step details if needed - we already have the run overview data
            const targetRun = targetGroup.runs.find(r => r.id === expandedRunId);
            if (targetRun && (!targetRun.steps || targetRun.steps.length === 0)) {
              try {
                const detailedRun = await api.getRunDetails(testId, expandedRunId);
                setRunGroups(prev => prev.map(g => 
                  g.id === expandedGroupId 
                    ? { 
                        ...g, 
                        runs: g.runs.map((r: any) => 
                          r.id === expandedRunId ? { ...detailedRun } : r
                        )
                      }
                    : g
                ));
              } catch (error) {
                console.error("Failed to load expanded run details:", error);
              }
            }
          }
        }
      } else {
        // Append mode for "Load More"
        setRunGroups(prev => [...prev, ...(response.groups || [])]);
      }
      
      setPagination(response.pagination);
      
      // Auto-expand first group if no group is currently expanded
      if (reset && !expandedGroupId && response.groups && response.groups.length > 0) {
        setExpandedGroupId(response.groups[0].id);
      }
      
      // Start polling if there are running tests
      const hasRunningTests = (response.groups || []).some(group => 
        group.runs?.some(run => run.state === 'RUNNING' || run.state === 'INIT')
      );
      
      if (hasRunningTests && !pollingInterval) {
        startPolling();
      } else if (!hasRunningTests && pollingInterval) {
        stopPolling();
      }
      
    } catch (error) {
      console.error("Failed to load runs:", error);
      showError("Failed to load runs", "Could not load test runs. Please try again.");
    } finally {
      setIsLoadingRuns(false);
      setIsPollingUpdate(false);
    }
  }, [testId, filters, pagination.offset, pagination.limit, pollingInterval, showError, expandedGroupId, expandedRunId, setExpandedGroupId, setExpandedRunId]);

  // Surgical polling that preserves UI state and expansion
  const updateRunStatus = useCallback(async () => {
    if (!testId) return;
    
    try {
      setIsPollingUpdate(true);
      
      // Dynamically read current URL params (not stale closure values)
      const currentParams = new URLSearchParams(window.location.search);
      const currentExpandedRunId = currentParams.get('runId');
      
      // Calculate how many groups we currently have to preserve "Load More" state
      const currentGroupCount = runGroups.length;
      const pollLimit = Math.max(pagination.limit, currentGroupCount);
      
      // Poll overview data - fetch enough to cover all currently loaded groups
      const response = await api.getRuns(testId, {
        offset: 0,
        limit: pollLimit,
        ...filters
      }) as RunsApiResponse;
      
      // If a run is currently expanded and running, also fetch its detailed step data
      let expandedRunData = null;
      if (currentExpandedRunId) {
        // Find the expanded run in the response
        const expandedRun = response.groups?.flatMap(g => g.runs).find(r => r.id === currentExpandedRunId);
        // Only fetch detailed data if the run is actively running (so we get step updates)
        if (expandedRun && (expandedRun.state === 'RUNNING' || expandedRun.state === 'INIT')) {
          try {
            expandedRunData = await api.getRunDetails(testId, currentExpandedRunId);
          } catch (error) {
            console.warn('Failed to fetch expanded run details during polling:', error);
          }
        }
      }
      
      setRunGroups(prevRunGroups => {
        const responseGroups = response.groups || [];
        const updatedGroups = [...prevRunGroups];
        
        // Surgically update existing groups while preserving state
        responseGroups.forEach(newGroup => {
          const existingIndex = updatedGroups.findIndex(g => g.id === newGroup.id);
          const existingGroup = existingIndex >= 0 ? updatedGroups[existingIndex] : null;
          
          // Update runs in this group, preserving all UI state
          const updatedRuns = newGroup.runs.map(newRun => {
            const existingRun = existingGroup?.runs.find(r => r.id === newRun.id);
            
            // If this is the expanded run and we have fresh detailed data from polling, use it
            if (expandedRunData && newRun.id === currentExpandedRunId) {
              return {
                ...expandedRunData,
                isLoadingDetails: false
              };
            }
            
            // If this run is URL-expanded but we don't have fresh data, preserve existing detailed data while updating overview data
            if (newRun.id === currentExpandedRunId && existingRun?.steps) {
              return {
                ...existingRun,        // Use existing detailed data (steps, etc)
                ...newRun,             // Update with new overview data (state, counts, etc)
                steps: existingRun.steps, // Ensure we keep the detailed steps
                isLoadingDetails: false
              };
            }
            
            // For non-expanded runs, just update the overview data
            return {
              ...newRun,
              isLoadingDetails: existingRun?.isLoadingDetails || false // Preserve loading state
            };
          });
          
          const updatedGroup = {
            ...newGroup,
            runs: updatedRuns
          };
          
          if (existingIndex >= 0) {
            // Update existing group in place
            updatedGroups[existingIndex] = updatedGroup;
          } else {
            // Add new group at the beginning (new runs typically appear first)
            updatedGroups.unshift(updatedGroup);
          }
        });
        
        // Remove groups that are no longer in the response (but preserve Load More data)
        const fetchedGroupIds = new Set(responseGroups.map(g => g.id));
        return updatedGroups.filter((group, index) => {
          if (fetchedGroupIds.has(group.id)) return true;
          // Keep groups beyond the fetched range to preserve Load More state
          if (index >= pollLimit) return true;
          return false;
        });
      });
      
      // Only update pagination if we're not preserving Load More state
      if (pollLimit === pagination.limit) {
        setPagination(response.pagination);
      }
      
      // Check if we should continue polling
      const hasRunningTests = (response.groups || []).some(group => 
        group.runs?.some(run => run.state === 'RUNNING' || run.state === 'INIT')
      );
      
      if (!hasRunningTests && pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
    } catch (error) {
      console.error("Failed to update run status:", error);
      // Don't show error to user for polling updates
    } finally {
      setIsPollingUpdate(false);
    }
  }, [testId, pagination.limit, filters, runGroups.length, pollingInterval]);
  
  // Polling for running tests
  const startPolling = useCallback(() => {
    if (pollingInterval) return;
    
    const interval = setInterval(() => {
      updateRunStatus(); // Use smart status update instead of full reload
    }, 5000); // Poll every 5 seconds
    
    setPollingInterval(interval);
  }, [updateRunStatus, pollingInterval]);
  
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [pollingInterval]);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);
  
  // Create run functionality
  const handleCreateRun = async () => {
    if (!testId) return;
    
    try {
      await api.createRun(testId, {
        numberOfRuns: newRunForm.numberOfRuns.toString(),
        automatedExecution: false
      });
      
      setShowCreateRunModal(false);
      setNewRunForm({ numberOfRuns: 1 });
      showSuccess("Run created successfully");
      
      // Reload runs to show new run
      await loadRuns(true, filters, false);
      
    } catch (error) {
      console.error("Failed to create run:", error);
      showError("Failed to create run", "Could not create new run. Please try again.");
    }
  };

  const handleFilter = () => {
    setShowFilterModal(true);
  };
  
  // Apply filters - will be used by the filter modal component
  const applyFilters = useCallback((newFilters: RunFilters) => {
    setFilters(newFilters);
    setShowFilterModal(false);
    loadRuns(true, newFilters);
  }, [loadRuns]);

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  // Run group management
  const toggleRunGroup = (group: RunGroup) => {
    if (!testId) return;
    
    if (expandedGroupId === group.id) {
      // Collapse group
      setExpandedGroupId(null);
      setExpandedRunId(null);
    } else {
      // Expand group - just update URL params, no API call needed
      // We already have all the run data from the initial load
      setExpandedGroupId(group.id);
    }
  };

  // Individual run management
  const toggleRun = async (run: TestRun, group: RunGroup) => {
    if (!testId || expandedGroupId !== group.id) return;
    
    if (expandedRunId === run.id) {
      setExpandedRunId(null);
      return;
    }
    
    // Check if we need detailed data - only fetch if steps are missing or empty
    const needsDetailedData = !run.steps || run.steps.length === 0;
    
    if (needsDetailedData) {
      try {
        // Start loading without showing immediate UI feedback (prevent flicker)
        let showLoadingTimeout: NodeJS.Timeout | null = null;
        
        // Only show loading state if it takes longer than 200ms
        showLoadingTimeout = setTimeout(() => {
          setRunGroups(prev => prev.map(g => ({
            ...g,
            runs: g.runs.map(r => 
              r.id === run.id ? { ...r, isLoadingDetails: true } : r
            )
          })));
        }, 200);
        
        const detailedRun = await api.getRunDetails(testId, run.id);
        
        // Cancel loading timeout if request completes quickly
        if (showLoadingTimeout) {
          clearTimeout(showLoadingTimeout);
        }
        
        // Update with detailed data
        setRunGroups(prev => prev.map(g => ({
          ...g,
          runs: g.runs.map(r => 
            r.id === run.id 
              ? { ...detailedRun, isLoadingDetails: false }
              : r
          )
        })));
        
      } catch (error) {
        console.error("Failed to load run details:", error);
        showError("Failed to load run details", "Could not load detailed run data.");
        
        setRunGroups(prev => prev.map(g => ({
          ...g,
          runs: g.runs.map(r => 
            r.id === run.id ? { ...r, isLoadingDetails: false } : r
          )
        })));
        return;
      }
    }
    
    setExpandedRunId(run.id);
  };

  // Confirmation handlers
  const confirmCancelRunGroup = (group: RunGroup) => {
    setSelectedGroup(group);
    setShowCancelModal(true);
  };

  const confirmRerunRunGroup = (group: RunGroup) => {
    setSelectedGroup(group);
    setShowRerunModal(true);
  };

  const confirmDeleteRunGroup = (group: RunGroup) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  // Action handlers
  const handleCancelRunGroup = async () => {
    if (!testId || !selectedGroup) return;
    
    try {
      setIsActionInProgress(true);
      await api.cancelRunGroup(testId, selectedGroup.id);
      showSuccess("Run group cancelled successfully");
      await loadRuns(true, filters, false);
      setShowCancelModal(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Failed to cancel run group:", error);
      showError("Failed to cancel run group", "Could not cancel the run group.");
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleRerunRunGroup = async () => {
    if (!testId || !selectedGroup) return;
    
    try {
      setIsActionInProgress(true);
      await api.rerunRunGroup(testId, selectedGroup.id);
      showSuccess("Run group rerun started successfully");
      await loadRuns(true, filters, false);
      setShowRerunModal(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Failed to rerun group:", error);
      showError("Failed to rerun group", "Could not start rerun for this group.");
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleDeleteRunGroup = async () => {
    if (!testId || !selectedGroup) return;
    
    try {
      setIsActionInProgress(true);
      await api.deleteRunGroup(testId, selectedGroup.id);
      showSuccess("Run group deleted successfully");
      await loadRuns(true, filters, false);
      setShowDeleteModal(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error("Failed to delete run group:", error);
      showError("Failed to delete run group", "Could not delete the run group.");
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleCloseModals = () => {
    setShowCancelModal(false);
    setShowRerunModal(false);
    setShowDeleteModal(false);
    setSelectedGroup(null);
  };

  // Run modal handlers
  const showRunDetails = (run: TestRun) => {
    setSelectedRun(run);
    setShowDetailsModal(true);
  };

  const showRunData = (run: TestRun) => {
    setSelectedRun(run);
    setShowRunDataModal(true);
  };

  const showStepDetails = (step: RunStep) => {
    setSelectedStep(step);
    setShowStepDetailsModal(true);
  };

  const showStepData = (step: RunStep) => {
    setSelectedStep(step);
    setShowStepDataModal(true);
  };

  // Expectation modal handlers
  const showExpectationDetails = (expectation: ExpectationResult) => {
    setSelectedExpectation(expectation);
    setShowExpectationDetailsModal(true);
  };

  const showExpectationData = (expectation: ExpectationResult) => {
    setSelectedExpectation(expectation);
    setShowExpectationDataModal(true);
  };

  const hideAllModals = () => {
    setShowLogsModal(false);
    setShowDetailsModal(false);
    setShowRunDataModal(false);
    setShowStepDetailsModal(false);
    setShowStepDataModal(false);
    setShowExpectationDetailsModal(false);
    setShowExpectationDataModal(false);
    setSelectedRun(null);
    setSelectedStep(null);
    setSelectedExpectation(null);
    setShowStepResultsJson(false);
  };

  // Utility functions
  const getRunStateClass = (item: TestRun | RunGroup | string, level: 'run' | 'group' = 'run') => {
    if (typeof item === 'string') {
      // Group state
      const group = runGroups.find(g => g.id === item);
      if (!group) return 'UNKNOWN';
      
      const runs = group.runs;
      if (runs.some(run => run.state === 'RUNNING' || run.state === 'INIT')) {
        return 'RUNNING';
      }
      if (runs.every(run => run.state === 'SUCCESS')) {
        return 'SUCCESS';
      }
      if (runs.every(run => run.state === 'FAILURE')) {
        return 'FAILURE';
      }
      return 'PARTIAL';
    }
    
    if (level === 'group') {
      const group = item as RunGroup;
      return group.state;
    }
    
    const run = item as TestRun;
    return run.state;
  };


  const getGroupDate = (group: RunGroup): string => {
    if (!group.runs || group.runs.length === 0) return 'Unknown date';
    
    // Find the earliest createdAt date from all runs in this group
    const dates = group.runs
      .map(run => run.createdAt)
      .filter(date => date)
      .sort();
    
    return dates.length > 0 ? dates[0] : 'Unknown date';
  };

  const getGroupHash = (groupId: string): number => {
    let hash = 0;
    if (groupId.length === 0) return hash;
    for (let i = 0; i < groupId.length; i++) {
      const char = groupId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };


  const hasRunningRuns = (runs: TestRun[]): boolean => {
    return runs.some(run => run.state === 'RUNNING' || run.state === 'INIT');
  };

  const allRunsFinished = (runs: TestRun[]): boolean => {
    return runs.every(run => run.state === 'SUCCESS' || run.state === 'FAILURE');
  };

  const getFilterStatusText = (): string => {
    const activeFilters = [];
    
    // Check if any filters are active
    const hasStateFilter = filters.state && filters.state !== 'All';
    const hasDateFilter = filters.dateRange && filters.dateRange !== 'all';
    const hasIdFilter = filters.runId && filters.runId.trim() !== '';
    
    // If no filters are active, show default text
    if (!hasStateFilter && !hasDateFilter && !hasIdFilter) {
      return 'Filter';
    }
    
    // Build filter description
    if (hasStateFilter) {
      activeFilters.push(filters.state);
    }
    
    if (hasDateFilter) {
      switch (filters.dateRange) {
        case 'today':
          activeFilters.push('Today');
          break;
        case 'yesterday':
          activeFilters.push('Yesterday');
          break;
        case 'last30':
          activeFilters.push('Last 30 days');
          break;
        case 'specific':
          if (filters.specificDate) {
            activeFilters.push(new Date(filters.specificDate).toLocaleDateString());
          }
          break;
        case 'range':
          if (filters.startDate && filters.endDate) {
            activeFilters.push(`${new Date(filters.startDate).toLocaleDateString()}-${new Date(filters.endDate).toLocaleDateString()}`);
          }
          break;
      }
    }
    
    if (hasIdFilter) {
      activeFilters.push(`ID: ${filters.runId}`);
    }
    
    return activeFilters.length > 0 ? activeFilters.join(', ') : 'Filter';
  };

  // Load test data and runs - ONLY when testId changes, not on every render
  useEffect(() => {
    if (testId) {
      loadTest();
      loadRuns(true, filters, false); // Initial load, not polling
    }
    // Intentionally excluding loadTest, loadRuns, filters from deps to prevent cascade of API calls
    // when expanding runs or changing other state. This effect should ONLY run when testId changes.
    // Filter changes are handled by applyFilters() function manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  if (isLoading && !test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        onClick={handleFilter}
        size="sm"
        className="text-xs"
      >
        <Filter className="w-4 h-4 mr-1" />
        {getFilterStatusText()}
      </Button>
      <Button
        onClick={() => setShowCreateRunModal(true)}
        size="sm"
        className="text-xs"
      >
        New Run
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="runs"
        onToggleChange={handleToggleChange}
        actions={headerActions}
      />

      <div className="flex-1 overflow-auto p-6">
        {/* Loading state - only show spinner for manual loads, not polling updates */}
        {isLoadingRuns && runGroups.length === 0 && !isPollingUpdate ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-sm text-muted-foreground">Loading runs...</div>
          </div>
        ) : runGroups.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-muted-foreground">
              <div className="text-sm">No test runs yet</div>
              <div className="text-xs mt-1">Click "New Run" to create one</div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {runGroups.map((group) => (
              <div key={group.id} className="border rounded-lg bg-card">
                {/* Group Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent"
                  onClick={() => toggleRunGroup(group)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      getRunStateClass(group.id, 'group') === 'RUNNING' ? 'bg-blue-500 animate-pulse' :
                      getRunStateClass(group.id, 'group') === 'SUCCESS' ? 'bg-green-500' :
                      getRunStateClass(group.id, 'group') === 'FAILURE' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`} />
                    <span className="text-sm font-medium">{formatDate(getGroupDate(group))}</span>
                    <span className="text-xs text-muted-foreground">(#{getGroupHash(group.id)})</span>
                    <span className="text-xs text-muted-foreground">({group.runs.length} run{group.runs.length > 1 ? 's' : ''})</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmCancelRunGroup(group);
                      }}
                      disabled={!hasRunningRuns(group.runs)}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmRerunRunGroup(group);
                      }}
                      disabled={!allRunsFinished(group.runs)}
                      className="text-xs"
                    >
                      Rerun
                    </Button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteRunGroup(group);
                      }}
                      className="px-2 py-1 text-xs text-destructive hover:bg-muted rounded-md cursor-pointer"
                      title="Delete group"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-muted-foreground">
                      {expandedGroupId === group.id ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {/* Group Content */}
                {expandedGroupId === group.id && (
                  <div className="border-t bg-muted/50">
                    {group.runs.map((run) => (
                      <div key={run.id} className="border-b last:border-b-0">
                          <div 
                            className={`flex items-center justify-between p-4 cursor-pointer hover:bg-accent transition-all duration-300 ${
                              recentlyUpdatedRuns.has(run.id) ? 'bg-blue-50 ring-1 ring-blue-200' : ''
                            }`}
                            onClick={() => toggleRun(run, group)}
                          >
                            <div className="flex items-center space-x-3 flex-1">
                              <div className={`w-2 h-2 rounded-full ${
                                run.state === 'RUNNING' ? 'bg-blue-500 animate-pulse' :
                                run.state === 'SUCCESS' ? 'bg-green-500' :
                                run.state === 'FAILURE' ? 'bg-red-500' :
                                'bg-yellow-500'
                              }`} />
                              <div className="text-sm flex-1">
                                <span className="text-green-600">{getPassedCount(run)} passed</span>
                                <span className="text-red-600 ml-2">{getFailedCount(run)} failed</span>
                                <span className="text-muted-foreground ml-2">(#{run.externalId})</span>
                                {run.log && <span className="text-muted-foreground ml-2">{run.log}</span>}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showRunData(run);
                                }}
                                className="px-2 py-1 text-xs hover:bg-muted rounded-md cursor-pointer"
                                title="View run data"
                              >
                                <Table className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showRunDetails(run);
                                }}
                                className="px-2 py-1 text-xs hover:bg-muted rounded-md cursor-pointer"
                                title="View details"
                              >
                                <Info className="w-4 h-4" />
                              </button>
                              <span className="text-sm text-muted-foreground">
                                {run.id === expandedRunId ? '▼' : '▶'}
                              </span>
                            </div>
                          </div>

                          {/* Run Details */}
                          {run.id === expandedRunId && (
                            <div className="bg-background border-t p-4">
                              {run.isLoadingDetails ? (
                                <div className="text-center text-sm text-muted-foreground">
                                  Loading run details...
                                </div>
                              ) : run.steps && run.steps.length > 0 ? (
                                <div className="space-y-2">
                                  {run.steps.map((step, stepIndex) => (
                                    <div 
                                      key={`${step.stepId}-${stepIndex}`} 
                                      className={`text-sm transition-all duration-300 ${
                                        recentlyUpdatedSteps.has(`${run.id}-${step.stepId || stepIndex}`) ? 
                                        'bg-yellow-50 border-l-4 border-yellow-300 pl-2 -ml-2' : ''
                                      }`}
                                    >
                                      <div className="mb-3">
                                        {/* Step name line with icons */}
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-3 flex-1">
                                            <div className={`w-2 h-2 rounded-full ${
                                              step.state === 'RUNNING' ? 'bg-blue-500 animate-pulse' :
                                              step.state === 'SUCCESS' ? 'bg-green-500' :
                                              step.state === 'FAILURE' ? 'bg-red-500' :
                                              step.state === 'INIT' ? 'bg-muted-foreground' :
                                              'bg-muted'
                                            }`} />
                                            <span className="font-medium">{step.name}</span>
                                            <span className="text-muted-foreground text-xs">({step.type})</span>
                                          </div>
                                          <div className="flex items-center space-x-1">
                                            {step.result && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  showStepData(step);
                                                }}
                                                className="px-1 py-1 hover:bg-muted rounded cursor-pointer"
                                                title="View step data"
                                              >
                                                <Table className="w-3 h-3" />
                                              </button>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                showStepDetails(step);
                                              }}
                                              className="px-1 py-1 hover:bg-muted rounded cursor-pointer"
                                              title="View step details"
                                            >
                                              <Info className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                        {/* Log messages below with left margin matching icon width */}
                                        {step.log && step.log !== 'Waiting to start' && (
                                          <div className="mt-0" style={{ marginLeft: '1.3rem' }}>
                                            <span className="text-muted-foreground text-xs">{step.log}</span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Expectation Results for TOOL_EVALUATE_EXPECTATIONS steps */}
                                      {step.type.startsWith('TOOL_EVALUATE_EXPECTATIONS') && 
                                        step.result?.expectations && 
                                        step.result.expectations.length > 0 && (
                                        <div className="mt-3 pl-5 space-y-2">
                                          {step.result.expectations.map((expectation: ExpectationResult, expectationIndex: number) => (
                                            <div key={`${expectation.id}-${expectationIndex}`} className="flex items-center justify-between">
                                              <div className="flex items-center space-x-3 flex-1">
                                                <div className={`w-2 h-2 rounded-full ${
                                                  expectation.runState === 'SUCCESS' && expectation.resultMatches ? 'bg-green-500' :
                                                  expectation.runState === 'FAILURE' || !expectation.resultMatches ? 'bg-red-500' :
                                                  expectation.runState === 'RUNNING' ? 'bg-blue-500 animate-pulse' :
                                                  expectation.runState === 'INIT' ? 'bg-muted-foreground' :
                                                  'bg-yellow-500'
                                                }`} />
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-medium">
                                                    <span className="text-muted-foreground">#{expectation.externalId}:</span>
                                                    <span className="ml-1">{expectation.expectation}</span>
                                                  </div>
                                                </div>
                                              </div>
                                              
                                              <div className="flex items-center space-x-1 ml-2">
                                                {expectation.data && expectation.data.length > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      showExpectationData(expectation);
                                                    }}
                                                    className="px-1 py-1 hover:bg-muted rounded cursor-pointer"
                                                    title="View expectation data"
                                                  >
                                                    <Table className="w-3 h-3" />
                                                  </button>
                                                )}
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    showExpectationDetails(expectation);
                                                  }}
                                                  className="px-1 py-1 hover:bg-muted rounded cursor-pointer"
                                                  title="View expectation details"
                                                >
                                                  <Info className="w-3 h-3" />
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center text-sm text-muted-foreground">
                                  No step details available
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            ))}

            {/* Load More Button */}
            {pagination.hasMore && (
              <div className="text-center py-4">
                <Button
                  variant="secondary"
                  onClick={() => loadRuns(false, filters, false)}
                  disabled={isLoadingRuns}
                >
                  {isLoadingRuns ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Create Run Modal */}
        {showCreateRunModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-popover rounded-lg shadow-xl p-6 w-full max-w-md border">
              <h3 className="text-lg font-semibold mb-4">Create New Run</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Number of Runs
                </label>
                <input
                  type="number"
                  min="1"
                  value={newRunForm.numberOfRuns}
                  onChange={(e) => setNewRunForm({ numberOfRuns: Number.parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreateRunModal(false);
                    setNewRunForm({ numberOfRuns: 1 });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateRun}
                  disabled={newRunForm.numberOfRuns < 1}
                >
                  Create
                </Button>
              </div>
          </div>
        </div>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <FilterModal 
            isOpen={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            onApply={applyFilters}
            currentFilters={filters}
          />
        )}

        {/* Confirmation Modals */}
        <ConfirmationModal
          isOpen={showCancelModal}
          onClose={handleCloseModals}
          onConfirm={handleCancelRunGroup}
          title="Cancel Run Group"
          message={`Are you sure you want to cancel all running tests in this group? This action will stop ${selectedGroup?.runs.filter(run => run.state === 'RUNNING' || run.state === 'INIT').length || 0} active runs.`}
          confirmText="Cancel Runs"
          cancelText="Keep Running"
          type="danger"
          isLoading={isActionInProgress}
        />

        <ConfirmationModal
          isOpen={showRerunModal}
          onClose={handleCloseModals}
          onConfirm={handleRerunRunGroup}
          title="Rerun Group"
          message={`Are you sure you want to rerun all ${selectedGroup?.runs.length || 0} tests in this group? This will create new runs with the same configuration.`}
          confirmText="Rerun Tests"
          cancelText="Cancel"
          type="info"
          isLoading={isActionInProgress}
        />

        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onConfirm={handleDeleteRunGroup}
          title="Delete Run Group"
          message={`Are you sure you want to delete this run group with ${selectedGroup?.runs.length || 0} runs? This action cannot be undone and will permanently remove all run data and history.`}
          confirmText="Delete Group"
          cancelText="Keep Group"
          type="danger"
          isLoading={isActionInProgress}
        />

        {/* Run Logs Modal */}
        {showLogsModal && selectedRun && (
          <RunLogsModal 
            run={selectedRun} 
            onClose={hideAllModals}
          />
        )}

        {/* Run Details Modal */}
        {showDetailsModal && selectedRun && (
          <RunDetailsModal 
            run={selectedRun} 
            onClose={hideAllModals}
          />
        )}

        {/* Run Data Modal */}
        {showRunDataModal && selectedRun && (
          <RunDataModal 
            run={selectedRun} 
            onClose={hideAllModals}
          />
        )}

        {/* Step Details Modal */}
        {showStepDetailsModal && selectedStep && (
          <StepDetailsModal 
            step={selectedStep} 
            onClose={hideAllModals}
          />
        )}

        {/* Step Data Modal */}
        {showStepDataModal && selectedStep && (
          <StepDataModal 
            step={selectedStep} 
            onClose={hideAllModals}
            showJson={showStepResultsJson}
            onToggleJson={() => setShowStepResultsJson(!showStepResultsJson)}
          />
        )}

        {/* Expectation Details Modal */}
        {showExpectationDetailsModal && selectedExpectation && (
          <ExpectationDetailsModal 
            expectation={selectedExpectation} 
            onClose={hideAllModals}
          />
        )}

        {/* Expectation Data Modal */}
        {showExpectationDataModal && selectedExpectation && (
          <ExpectationDataModal 
            expectation={selectedExpectation} 
            onClose={hideAllModals}
          />
        )}
      </div>
    </div>
  );
}

